import * as React from "react";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  clampPaginationToPageCount,
  paginationAfterFilterOrSortChange,
} from "./data-table-pagination";

// Server-side pagination props
interface ServerPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  /** Unit label after the count, e.g. "submissions" or "held evidence". Defaults to "rows". */
  totalCountUnit?: string;
  /** Optional footnote below the range summary (e.g. pinned held rows on page 1). */
  paginationNote?: string;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

// Server-side search props
interface ServerSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
}

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchKey?: string;
  searchPlaceholder?: string;
  showColumnToggle?: boolean;
  onRowClick?: (row: TData) => void;
  onSelectionChange?: (selectedRows: TData[]) => void;
  isLoading?: boolean;
  selectionResetKey?: number;
  disablePagination?: boolean;
  getRowClassName?: (row: TData) => string;
  // Server-side pagination (optional - if not provided, uses client-side)
  serverPagination?: ServerPaginationProps;
  // Server-side search (optional - if provided, uses controlled search that calls server)
  serverSearch?: ServerSearchProps;
  initialColumnVisibility?: VisibilityState;
  /** When set, column visibility is restored from and saved to localStorage. */
  columnVisibilityStorageKey?: string;
}

function readStoredColumnVisibility(storageKey: string): VisibilityState | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return null;

    const visibility: VisibilityState = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") {
        visibility[key] = value;
      }
    }
    return visibility;
  } catch {
    return null;
  }
}

function writeStoredColumnVisibility(storageKey: string, visibility: VisibilityState): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(visibility));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

function getColumnToggleLabel<TData>(column: { id: string; columnDef: ColumnDef<TData, unknown> }): string {
  const meta = column.columnDef.meta as { title?: string } | undefined;
  return meta?.title ?? column.id;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = "Search...",
  showColumnToggle = true,
  onRowClick,
  onSelectionChange,
  isLoading = false,
  selectionResetKey,
  disablePagination = false,
  getRowClassName,
  serverPagination,
  serverSearch,
  initialColumnVisibility,
  columnVisibilityStorageKey,
}: DataTableProps<TData, TValue>) {
  const defaultColumnVisibility = React.useMemo(
    () => initialColumnVisibility ?? {},
    [initialColumnVisibility],
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => {
    if (!columnVisibilityStorageKey) {
      return defaultColumnVisibility;
    }

    const stored = readStoredColumnVisibility(columnVisibilityStorageKey);
    if (!stored) {
      return defaultColumnVisibility;
    }

    return { ...defaultColumnVisibility, ...stored };
  });
  const [rowSelection, setRowSelection] = React.useState({});
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [localSearchValue, setLocalSearchValue] = React.useState(serverSearch?.value ?? "");
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    if (serverSearch) setLocalSearchValue(serverSearch.value);
  }, [serverSearch?.value]);

  const handleServerSearchChange = React.useCallback(
    (value: string) => {
      setLocalSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      const debounceMs = serverSearch?.debounceMs ?? 300;
      if (debounceMs <= 0) {
        serverSearch?.onChange(value);
        return;
      }
      debounceRef.current = setTimeout(() => {
        serverSearch?.onChange(value);
      }, debounceMs);
    },
    [serverSearch],
  );

  React.useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const isServerSide = !!serverPagination;
  const shouldPaginate = !disablePagination && !isServerSide;
  const serverRangeStart = serverPagination && serverPagination.totalCount > 0
    ? ((serverPagination.currentPage - 1) * serverPagination.pageSize) + 1
    : 0;
  const serverRangeEnd = serverPagination
    ? Math.min(serverPagination.currentPage * serverPagination.pageSize, serverPagination.totalCount)
    : 0;

  React.useEffect(() => {
    if (selectionResetKey !== undefined) {
      setRowSelection({});
    }
  }, [selectionResetKey]);

  // Keep the current page across parent rerenders (modals, etc). TanStack
  // defaults to autoResetPageIndex, which treats a new `data` array as a reset.
  const skipFilterPageResetRef = React.useRef(true);
  React.useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setPagination(paginationAfterFilterOrSortChange);
  }, [columnFilters, sorting]);

  const handleColumnVisibilityChange = React.useCallback(
    (updater: React.SetStateAction<VisibilityState>) => {
      setColumnVisibility((previous) => {
        const next = typeof updater === "function" ? updater(previous) : updater;
        if (columnVisibilityStorageKey) {
          writeStoredColumnVisibility(columnVisibilityStorageKey, next);
        }
        return next;
      });
    },
    [columnVisibilityStorageKey],
  );

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // Only use client-side pagination if not disabled and not using server-side
    ...(shouldPaginate ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: shouldPaginate ? setPagination : undefined,
    autoResetPageIndex: false,
    // For server-side, disable built-in pagination
    manualPagination: isServerSide || disablePagination,
    pageCount: isServerSide ? serverPagination.totalPages : undefined,
    initialState: disablePagination ? { pagination: { pageSize: 10000 } } : undefined,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(shouldPaginate ? { pagination } : {}),
    },
  });

  const pageCount = table.getPageCount();
  React.useEffect(() => {
    if (!shouldPaginate || pageCount <= 0 || pagination.pageIndex < pageCount) {
      return;
    }
    setPagination((previous) => clampPaginationToPageCount(previous, pageCount));
  }, [shouldPaginate, pageCount, pagination.pageIndex]);

  // Notify parent of selection changes
  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map(row => row.original);
      onSelectionChange(selectedRows);
    }
  }, [rowSelection, table, onSelectionChange]);

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between gap-4">
        {/* Server-side search takes precedence over client-side search */}
        {serverSearch ? (
          <Input
            placeholder={serverSearch.placeholder || searchPlaceholder}
            value={localSearchValue}
            onChange={(event) => handleServerSearchChange(event.target.value)}
            className="max-w-sm"
          />
        ) : searchKey ? (
          <Input
            placeholder={searchPlaceholder}
            value={(table.getColumn(searchKey)?.getFilterValue() as string) ?? ""}
            onChange={(event) =>
              table.getColumn(searchKey)?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
        ) : null}
        {showColumnToggle && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {getColumnToggleLabel(column)}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup, groupIndex) => (
              <TableRow key={headerGroup.id} className="border-b border-gray-300">
                {headerGroup.headers.map((header, headerIndex) => {
                  // Check if this is a grouped header (has sub-columns)
                  const isGroupHeader = header.subHeaders && header.subHeaders.length > 0;
                  // Add right border except for last column
                  const isLastHeader = headerIndex === headerGroup.headers.length - 1;
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`bg-gray-100 ${isGroupHeader ? "text-center font-semibold" : "font-medium"} ${!isLastHeader ? "border-r border-gray-300" : ""}`}
                      style={{ backgroundColor: '#f3f4f6' }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => onRowClick?.(row.original)}
                  className={`border-b border-gray-200 ${onRowClick ? "cursor-pointer" : ""} ${getRowClassName ? getRowClassName(row.original) : ""}`}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const isLastCell = cellIndex === row.getVisibleCells().length - 1;
                    return (
                      <TableCell
                        key={cell.id}
                        className={!isLastCell ? "border-r border-gray-200" : ""}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {/* Pagination Controls */}
      {isServerSide ? (
        // Server-side pagination UI
        <div className="flex items-center justify-between py-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length > 0 && (
              <span className="mr-2">
                {table.getFilteredSelectedRowModel().rows.length} selected •{" "}
              </span>
            )}
            <span>
              Showing {serverRangeStart}–
              {serverRangeEnd} of{" "}
              {serverPagination.totalCount} {serverPagination.totalCountUnit ?? "rows"}
            </span>
            {serverPagination.paginationNote ? (
              <span className="block text-xs text-muted-foreground mt-0.5">
                {serverPagination.paginationNote}
              </span>
            ) : null}
          </div>
          <div className="flex items-center space-x-6 lg:space-x-8">
            {/* Page size selector */}
            {serverPagination.onPageSizeChange && (
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium">Rows per page</p>
                <Select
                  value={String(serverPagination.pageSize)}
                  onValueChange={(value) => serverPagination.onPageSizeChange?.(Number(value))}
                >
                  <SelectTrigger className="h-8 w-[70px]">
                    <SelectValue placeholder={serverPagination.pageSize} />
                  </SelectTrigger>
                  <SelectContent side="top">
                    {(serverPagination.pageSizeOptions || [10, 20, 30, 50, 100]).map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Page indicator */}
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Page {serverPagination.currentPage} of {serverPagination.totalPages}
            </div>
            {/* Navigation buttons */}
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => serverPagination.onPageChange(1)}
                disabled={serverPagination.currentPage <= 1 || isLoading}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => serverPagination.onPageChange(serverPagination.currentPage - 1)}
                disabled={serverPagination.currentPage <= 1 || isLoading}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => serverPagination.onPageChange(serverPagination.currentPage + 1)}
                disabled={serverPagination.currentPage >= serverPagination.totalPages || isLoading}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => serverPagination.onPageChange(serverPagination.totalPages)}
                disabled={serverPagination.currentPage >= serverPagination.totalPages || isLoading}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : shouldPaginate ? (
        // Client-side pagination UI
        <div className="flex items-center justify-end space-x-2 py-4">
          <div className="flex-1 text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length > 0 && (
              <span className="mr-2">
                {table.getFilteredSelectedRowModel().rows.length} of{" "}
              </span>
            )}
            {table.getFilteredRowModel().rows.length} row(s) total
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage() || isLoading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage() || isLoading}
            >
              Next
            </Button>
          </div>
        </div>
      ) : (
        // No pagination - show row count only
        <div className="flex items-center justify-end py-4">
          <div className="text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length > 0 && (
              <span className="mr-2">
                {table.getFilteredSelectedRowModel().rows.length} selected •{" "}
              </span>
            )}
            {table.getFilteredRowModel().rows.length} row(s) total
          </div>
        </div>
      )}
    </div>
  );
}
